if (!customElements.get("collections-filtering")) {
  /* Fitment rules live in snippets/bf-fitment-core.liquid (window.BFFitment).
     If the snippet is missing, the picker still works, just without blocking. */
  const BF = window.BFFitment || null;

  const isRealOption = (option) =>
    option.value !== "" && !option.classList.contains("default");

  const hasProducts = (option) =>
    option.dataset.count === undefined || Number(option.dataset.count) > 0;

  class CollectionsFiltering extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector("form");
      this.button = this.querySelector('button[type="submit"]');
      this.select = this.querySelector("select.dropdown-collection");
      this.input = this.querySelector("input.input-collection");
      this.selects = this.querySelectorAll("select.dropdown-filter");

      this.requestId = 0;
      this.controller = null;

      this.setupEventListeners();

      if (!this.select) {
        this.populateCollectionFilters(true);
      }
    }

    setupEventListeners() {
      this.form.addEventListener("submit", (event) => {
        event.preventDefault();
        this.rememberVehicle();
        window.location = this.form.getAttribute("action");
      });
      this.select?.addEventListener("change", this.onCollectionChange.bind(this));
      this.selects.forEach((select) => {
        select.addEventListener("change", this.onFilterChange.bind(this));
      });
    }

    /* Memory is written only here: an explicit "search" with the picker. */
    rememberVehicle() {
      if (!BF) return;
      const config = {};
      this.querySelectorAll("select.dropdown-filter:enabled").forEach((select) => {
        const option = select.selectedOptions[0];
        if (!option || !select.value) return;
        const paramName = option.dataset.paramName;
        if (paramName && BF.isVehicleParam(paramName)) {
          config[paramName] = option.value;
        }
      });
      if (Object.keys(config).length > 0) {
        BF.saveConfig(config);
      }
    }

    selectName(select) {
      const option = select.selectedOptions[0];
      return (option && option.dataset.paramName) || select.dataset.label || "";
    }

    getCurrentSelections() {
      const selections = [];
      this.selects.forEach((select) => {
        if (select.disabled || !select.value) return;
        selections.push({ name: this.selectName(select), value: select.value });
      });
      return selections;
    }

    filterCompatibleOptions(options, fallbackName) {
      const list = Array.from(options);
      if (!BF) return list;
      const selections = this.getCurrentSelections();
      return list.filter(
        (option) =>
          !BF.isOptionBlocked(
            option.dataset.paramName || fallbackName,
            option.value,
            selections,
          ),
      );
    }

    constructURL() {
      const collectionUrl =
        this.select?.value ?? this.input?.value ?? window.routes.all_products_url;
      const url = new URL(collectionUrl, window.requestShopUrl);

      this.querySelectorAll("select.dropdown-filter:enabled").forEach((select) => {
        if (select.value === "") {
          return;
        }

        Array.from(select.selectedOptions).forEach((option) => {
          url.searchParams.set(option.dataset.paramName, option.value);
        });
      });

      return url.toString();
    }

    setFormUrl(url, background) {
      this.form.setAttribute("action", url);

      if (!background) {
        this.button.disabled = false;
      }
    }

    selectRemoveOptions(select) {
      select.querySelectorAll("option:not(.default)").forEach((option) => {
        select.removeChild(option);
      });

      const first = select.querySelector("option");
      if (first) first.selected = true;
    }

    replaceOptions(select, options) {
      this.selectRemoveOptions(select);
      if (options.length > 0) {
        select.append(...options);
      }
      const defaultOption = select.querySelector("option.default");
      if (defaultOption) defaultOption.selected = true;
    }

    setGroupEnabled(select, enabled) {
      select.disabled = !enabled;
      select.closest(".form-group")?.classList.toggle("disabled", !enabled);
    }

    setLoading(isLoading) {
      this.classList.toggle("is-loading", isLoading);
      this.button.classList.toggle("loading", isLoading);
    }

    /* One request at a time: a newer request aborts the older one, and a
       stale response is ignored even if it arrives after the abort. */
    fetchFilterMarkup() {
      if (this.controller) this.controller.abort();
      this.controller = typeof AbortController === "function" ? new AbortController() : null;
      const requestId = ++this.requestId;

      const url = new URL(this.constructURL());
      url.searchParams.set("view", "do_not_use");

      const promise = fetch(url, { signal: this.controller?.signal })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Filter request failed with status ${response.status}`);
          }
          return response.text();
        })
        .then((text) =>
          requestId === this.requestId
            ? new DOMParser().parseFromString(text, "text/html")
            : null,
        );

      return { requestId, promise };
    }

    handleFetchError(error, requestId, showedLoading) {
      if (error && error.name === "AbortError") return;
      if (requestId !== this.requestId) return;
      if (showedLoading) this.setLoading(false);
      console.error("[collections-filtering]", error);
    }

    optionsFor(resultsMarkup, select) {
      const label = select.dataset.label || "";
      const options = resultsMarkup.querySelectorAll(
        `select[data-label="${CSS.escape(label)}"] option`,
      );
      const available = Array.from(options).filter(
        (option) => isRealOption(option) && hasProducts(option),
      );
      return this.filterCompatibleOptions(available, label);
    }

    onFilterChange(event) {
      const selectedIndex = Number(event.target.dataset.index);

      this.selects.forEach((select) => {
        if (Number(select.dataset.index) <= selectedIndex) return;
        this.selectRemoveOptions(select);
        this.setGroupEnabled(select, false);
      });

      this.setFormUrl(this.constructURL());
      this.setLoading(true);

      const { requestId, promise } = this.fetchFilterMarkup();

      promise
        .then((resultsMarkup) => {
          if (!resultsMarkup) return;
          this.setLoading(false);

          this.selects.forEach((select) => {
            const index = Number(select.dataset.index);
            if (index <= selectedIndex) return;

            const options = this.optionsFor(resultsMarkup, select);
            this.replaceOptions(select, options);

            if (index === selectedIndex + 1 && options.length > 0) {
              this.setGroupEnabled(select, true);
            }
          });
        })
        .catch((error) => this.handleFetchError(error, requestId, true));
    }

    populateCollectionFilters(background = false) {
      this.setFormUrl(this.constructURL(), background);

      if (!background) {
        this.setLoading(true);
      }

      const { requestId, promise } = this.fetchFilterMarkup();

      promise
        .then((resultsMarkup) => {
          if (!resultsMarkup) return;
          if (!background) {
            this.setLoading(false);
          }

          this.selects.forEach((select) => {
            const options = this.optionsFor(resultsMarkup, select);
            this.replaceOptions(select, options);

            if (Number(select.dataset.index) === 1) {
              this.setGroupEnabled(select, true);
            }
          });
        })
        .catch((error) => this.handleFetchError(error, requestId, !background));
    }

    onCollectionChange() {
      this.selects.forEach((select) => {
        this.selectRemoveOptions(select);
        this.setGroupEnabled(select, false);
      });

      this.populateCollectionFilters();
    }
  }

  customElements.define("collections-filtering", CollectionsFiltering);
}
